import React, { useEffect, useRef, useState } from "react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "./ui/sheet";
import { Send, MessageSquare, User as UserIcon } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

function relTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function CommentsSheet({ product, open, onOpenChange, onCountChange }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [canComment, setCanComment] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    if (!product) return;
    try {
      const { data } = await api.get(`/products/${product.product_id}/comments`);
      setComments(data);
      onCountChange?.(data.length);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line
  }, [open, product?.product_id]);

  // Eligibility check — must have a completed order for this product
  useEffect(() => {
    if (!open || !product || !user) { setCanComment(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/can-interact", { params: { product_id: product.product_id } });
        if (!cancelled) setCanComment(!!data.allowed);
      } catch {
        if (!cancelled) setCanComment(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, product?.product_id, user]);

  const send = async (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t) return;
    if (!user) { toast.error("Log in to comment"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/products/${product.product_id}/comments`, { text: t });
      setComments((c) => [data, ...c]);
      setText("");
      onCountChange?.(comments.length + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't comment");
    } finally {
      setSending(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="bg-zinc-950 border-t border-white/10 text-white p-0 h-[70vh] rounded-t-3xl"
        data-testid="comments-sheet"
      >
        <SheetHeader className="px-5 py-4 border-b border-white/10">
          <SheetTitle className="text-base font-black tracking-tight text-white inline-flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-pink-400" />
            {comments.length} {comments.length === 1 ? "comment" : "comments"}
          </SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="overflow-y-auto px-4 py-3 space-y-3" style={{ maxHeight: "calc(70vh - 130px)" }}>
          {comments.length === 0 && (
            <div className="text-center py-12 text-zinc-500 text-sm" data-testid="empty-comments">
              Be the first to comment.
            </div>
          )}
          {comments.map((c) => (
            <div key={c.comment_id} className="flex gap-3" data-testid={`comment-${c.comment_id}`}>
              <div className="w-8 h-8 rounded-full bg-pink-500/20 grid place-items-center overflow-hidden shrink-0 border border-pink-400/20">
                {c.user_avatar ? (
                  <img src={c.user_avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <UserIcon className="w-4 h-4 text-pink-200" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-zinc-400 font-bold">
                  {c.user_name} <span className="ml-1 text-zinc-600 font-normal">{relTime(c.created_at)}</span>
                </p>
                <p className="text-sm text-white mt-0.5 leading-snug">{c.text}</p>
              </div>
            </div>
          ))}
        </div>

        <form onSubmit={send} className="absolute bottom-0 left-0 right-0 px-4 py-3 border-t border-white/10 flex items-center gap-2 bg-zinc-950">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              !user
                ? "Log in to comment"
                : canComment
                ? "Add a comment…"
                : "Order this gem to comment"
            }
            disabled={!user || !canComment}
            className="flex-1 h-11 px-4 rounded-full bg-white/5 border border-white/10 text-white text-sm placeholder:text-zinc-500 outline-none focus:border-pink-400 disabled:opacity-60"
            data-testid="comment-input"
          />
          <button
            type="submit"
            disabled={sending || !text.trim() || !user || !canComment}
            className="h-11 w-11 rounded-full bg-pink-500 hover:bg-pink-400 grid place-items-center disabled:opacity-40 glow-pink"
            aria-label="Send comment"
            data-testid="comment-send-btn"
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </form>
      </SheetContent>
    </Sheet>
  );
}
